import { SocioRow } from '../../constitution-form/models/socio-row';

type ShareNumbering = NonNullable<SocioRow['shareNumberings']>[number];

/** Holding of the transmitente that is given a fecha de baja. */
export interface TransmisionBaja {
  seriesId: number | null;
  desde: number;
  hasta: number;
}

export interface TransmisionAdquirente {
  clientId: number;
  rangos: Array<{ seriesId: number | null; seriesName?: string; desde: number; hasta: number }>;
}

export interface TransmisionParams {
  transmitenteId: number | null;
  bajas: TransmisionBaja[];
  adquirentes: TransmisionAdquirente[];
}

function withTotals(socio: SocioRow, shareNumberings: ShareNumbering[]): SocioRow {
  return {
    ...socio,
    shareNumberings,
    sharesCount: shareNumberings.reduce((sum, n) => sum + (n.sharesCount ?? 0), 0),
  };
}

/** Removes [desde, hasta] from a holding of the same series, splitting it when the range falls inside. */
function subtractRange(n: ShareNumbering, seriesId: number | null, desde: number, hasta: number): ShareNumbering[] {
  const from = n.sharesNumberFrom, to = n.sharesNumberTo;
  if ((n.seriesId ?? null) !== (seriesId ?? null) || from == null || to == null || hasta < from || desde > to) return [n];
  const piece = (a: number, b: number): ShareNumbering => ({ ...n, sharesNumberFrom: a, sharesNumberTo: b, sharesCount: b - a + 1 });
  return [
    ...(desde > from ? [piece(from, desde - 1)] : []),
    ...(hasta < to ? [piece(hasta + 1, to)] : []),
  ];
}

/**
 * Applies a transmisión to the socios list: removes the holdings given a fecha de baja
 * (dropping the transmitente when none remain) and adds the acquired ranges to each adquirente,
 * creating the socio with `makeSocio` when they are not partners yet.
 */
export function applyTransmision(
  socios: SocioRow[],
  params: TransmisionParams,
  makeSocio: (clientId: number) => SocioRow,
): SocioRow[] {
  let result = socios.flatMap(socio => {
    if (params.transmitenteId == null || socio.associatedClient?.id !== params.transmitenteId) return [socio];
    const remaining = (socio.shareNumberings ?? []).filter(n =>
      !params.bajas.some(b =>
        (b.seriesId ?? null) === (n.seriesId ?? null)
        && b.desde === n.sharesNumberFrom
        && b.hasta === n.sharesNumberTo),
    );
    // What the adquirentes receive leaves the transmitente too (a sale of 1–100 out of 1–1000 keeps 101–1000).
    const sold = params.adquirentes.flatMap(a => a.rangos).filter(r => Number.isFinite(r.desde) && Number.isFinite(r.hasta));
    const kept = sold.length === 0 ? remaining
      : remaining.flatMap(n => sold.reduce<ShareNumbering[]>((parts, r) => parts.flatMap(p => subtractRange(p, r.seriesId, r.desde, r.hasta)), [n]));
    return kept.length === 0 && (params.bajas.length > 0 || (sold.length > 0 && remaining.length > 0)) ? [] : [withTotals(socio, kept)];
  });

  for (const adq of params.adquirentes) {
    const added: ShareNumbering[] = adq.rangos
      .filter(r => Number.isFinite(r.desde) && Number.isFinite(r.hasta) && r.hasta >= r.desde)
      .map(r => ({
        seriesId: r.seriesId,
        seriesName: r.seriesName,
        sharesCount: r.hasta - r.desde + 1,
        sharesNumberFrom: r.desde,
        sharesNumberTo: r.hasta,
        titulo: 'plena_propiedad',
        ownershipPercentage: 100,
      }));
    if (added.length === 0) continue;

    const idx = result.findIndex(s => s.associatedClient?.id === adq.clientId);
    const base = idx >= 0 ? result[idx] : makeSocio(adq.clientId);
    const updated = withTotals(base, [...(base.shareNumberings ?? []), ...added]);
    result = idx >= 0 ? result.map((s, i) => (i === idx ? updated : s)) : [...result, updated];
  }
  return result;
}
